import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';

export interface WinnerEntry {
    rank: 1 | 2 | 3;
    nickname: string;
    email: string;
    score: number;
    /** 표시용: 있으면 이메일 대신 표시 (이메일 아이디 또는 아이디) */
    id?: string;
}

/** 이메일 아이디(@ 앞부분) 또는 id 반환 */
function getEmailOrIdDisplay(entry: WinnerEntry): string {
    if (entry.id?.trim()) return entry.id.trim();
    const at = entry.email?.indexOf('@');
    return at > 0 ? entry.email!.slice(0, at) : (entry.email || '');
}

interface Top3WinnersProps {
    /** [1등, 2등, 3등] 순서로 전달. 표시는 2등-1등-3등(좌-중-우) */
    winners: WinnerEntry[];
}

const BAR_HEIGHTS = { 1: 'h-56', 2: 'h-40', 3: 'h-28' };
const BAR_COLORS = {
    1: 'bg-gradient-to-t from-[#7FCFFE] to-blue-600',
    2: 'bg-gradient-to-t from-[#7FCFF2] to-[#7FCFFE]',
    3: 'bg-gradient-to-t from-gray-300 to-gray-400'
};

/** 꽃가루 색상 (연한 노랑, 핑크, 화이트) */
const POLLEN_COLORS = ['#FFBF00', '#00D84D', '#f9a8d4', '#fce7f3', '#FFC5C6', '#E4271C'];
const POLLEN_COUNT = 24;

function PollenParticles(): React.ReactElement {
    const particles = useMemo(() => {
        return Array.from({ length: POLLEN_COUNT }, (_, i) => ({
            id: i,
            left: (i * 13 + 7) % 100,
            size: 12 + (i % 6),
            color: POLLEN_COLORS[i % POLLEN_COLORS.length],
            duration: 10 + (i % 6),
            delay: (i * 0.5) % 10,
        }));
    }, []);
    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes pollen-fall {
                    0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0.85; }
                    100% { transform: translateY(420px) translateX(30px) rotate(200deg); opacity: 0.25; }
                }
            `}} />
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl z-[1]" aria-hidden>
                {particles.map((p) => (
                    <div
                        key={p.id}
                        className="absolute rounded-full will-change-transform"
                        style={{
                            top: 0,
                            left: `${p.left}%`,
                            width: p.size,
                            height: p.size,
                            backgroundColor: p.color,
                            animation: `pollen-fall ${p.duration}s linear ${p.delay}s infinite`,
                        }}
                    />
                ))}
            </div>
        </>
    );
}

export const Top3Winners: React.FC<Top3WinnersProps> = ({ winners }) => {
    const { t } = useTranslation('class');
    const ordered = [
        winners.find((w) => w.rank === 2),
        winners.find((w) => w.rank === 1),
        winners.find((w) => w.rank === 3)
    ].filter(Boolean) as WinnerEntry[];

    if (ordered.length === 0) return null;

    return (
        <div className="relative rounded-xl border border-gray-200 pt-24 shadow-sm overflow-hidden" style={{ backgroundColor: '#F1F8FF' }}>
            <PollenParticles />
            <div className="relative flex items-end justify-center gap-6 max-w-2xl mx-auto">
                {ordered.map((w) => (
                    <div key={w.rank} className="flex-1 flex flex-col items-center min-w-0">
                        <div className="w-full text-center space-y-1 mb-2">
                            <p className="text-xl font-bold text-gray-900 truncate w-full" title={w.nickname}>
                                {w.nickname}
                            </p>
                            <p className="text-sm text-gray-500 truncate w-full" title={w.email || w.id}>
                                {getEmailOrIdDisplay(w)}
                            </p>
                            <p className="text-base font-bold text-gray-900">
                                {t('simulationResults.top3Score', { score: w.score })}
                            </p>
                        </div>
                        <div
                            className={`w-full ${BAR_HEIGHTS[w.rank]} ${BAR_COLORS[w.rank]}  flex flex-col items-center justify-center py-3 gap-2`}
                        >
                            <span className="text-white font-extrabold text-2xl">
                                {t('simulationResults.top3TopRank', { rank: w.rank })}
                            </span>
                            {w.rank === 1 && (
                                <Trophy size={48} className="text-amber-300 drop-shadow flex-shrink-0" aria-hidden />
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
